import { Injectable } from '@angular/core';

import * as ext from '@app/extensions';

// Main
const SL_MATH_EPSILON: number = 0.0001;
const fltEpsilon: number = parseFloat('1.192092896e-07F');

const castFloatToInt = (value) => {
  return value - (value % 1);
};

const floatLt = (a, b) => {
  return a < b + SL_MATH_EPSILON;
};

const floatGt = (a, b) => {
  return a > b + SL_MATH_EPSILON;
};

const floatEq = (a, b) => {
  return a >= b - SL_MATH_EPSILON && a <= b + SL_MATH_EPSILON;
};

const lineIntersectsLine = (a0, a1, b0, b1) => {
  const d = (a1.x - a0.x) * (b1.y - b0.y) - (b1.x - b0.x) * (a1.y - a0.y);

  if (d === 0) {
    return false;
  }

  const r = ((b1.x - b0.x) * (a0.y - b0.y) - (a0.x - b0.x) * (b1.y - b0.y)) / d;

  if (r < 0 || r > 1) {
    return false;
  }

  const s = ((a1.x - a0.x) * (a0.y - b0.y) - (a0.x - b0.x) * (a1.y - a0.y)) / d;

  if (s < 0 || s > 1) {
    return false;
  }

  return true;
};

const lineIntersectsRect = (a, b, rect) => {
  if (rect.containsPoint(a) || rect.containsPoint(b)) {
    return true;
  }

  if (lineIntersectsLine(a, b, rect.left_top(), rect.right_top())) {
    return true;
  }

  if (lineIntersectsLine(a, b, rect.right_top(), rect.right_bottom())) {
    return true;
  }

  if (lineIntersectsLine(a, b, rect.right_bottom(), rect.left_bottom())) {
    return true;
  }

  if (lineIntersectsLine(a, b, rect.left_bottom(), rect.left_top())) {
    return true;
  }

  return false;
};

// Size
export class SizeModel {
  constructor(
    public width = 0,
    public height = 0,
  ) {}

  isAreaEmpty(): boolean {
    return this.width === 0 || this.height === 0;
  }

  copy(): SizeModel {
    return new SizeModel(this.width, this.height);
  }

  isEqual(value): boolean {
    return value && this.width === value.width && this.height === value.height;
  }

  isEmpty(): boolean {
    return this.width === 0 && this.height === 0;
  }

  floor(): SizeModel {
    return new SizeModel(castFloatToInt(this.width), castFloatToInt(this.height));
  }

  round(): SizeModel {
    return new SizeModel(castFloatToInt(Math.ceil(this.width)), castFloatToInt(Math.ceil(this.height)));
  }
}

// Matrix
export class MatrixModel {
  constructor(
    public a,
    public b,
    public c,
    public d,
    public e,
    public f,
  ) {}

  determinant(): number {
    return this.a * this.d - this.b * this.c;
  }

  invert(): MatrixModel {
    const det = this.determinant();

    if (det > -fltEpsilon && det < fltEpsilon) {
      return null;
    }

    const scale = 1.0 / det;

    const a = this.d * scale;
    const b = -this.b * scale;
    const c = -this.c * scale;
    const d = this.a * scale;
    const e = this.f * (this.c * scale) - this.e * (this.d * scale);
    const f = this.e * (this.b * scale) - this.f * (this.a * scale);

    return new MatrixModel(a, b, c, d, e, f);
  }

  mapPoint(point) {
    const x = point.x;
    const y = point.y;

    return new PointModel(this.a * x + this.c * y + this.e, this.b * x + this.d * y + this.f);
  }

  mapRect(rect) {
    const points = [];

    points[0] = this.mapPoint({ x: rect.left, y: rect.top });
    points[1] = this.mapPoint({ x: rect.right, y: rect.top });
    points[2] = this.mapPoint({ x: rect.right, y: rect.bottom });
    points[3] = this.mapPoint({ x: rect.left, y: rect.bottom });

    const result = {
      left: points[0].x,
      top: points[0].y,
      right: points[0].x,
      bottom: points[0].y,
    };

    for (let i = 1; i < 4; ++i) {
      result.left = Math.min(result.left, points[i].x);
      result.top = Math.min(result.top, points[i].y);
      result.right = Math.max(result.right, points[i].x);
      result.bottom = Math.max(result.bottom, points[i].y);
    }

    return new RectModel(result.left, result.top, result.right, result.bottom);
  }

  mapSize(size) {
    return new SizeModel(this.a * size.width + this.c * size.height, this.b * size.width + this.d * size.height);
  }

  copy() {
    return new MatrixModel(this.a, this.b, this.c, this.d, this.e, this.f);
  }

  multiply(that) {
    const na = this.a * that.a + this.b * that.c;
    const nb = this.a * that.b + this.b * that.d;
    const nc = this.c * that.a + this.d * that.c;
    const nd = this.c * that.b + this.d * that.d;
    const ne = this.e * that.a + this.f * that.c + that.e;
    const nf = this.e * that.b + this.f * that.d + that.f;

    return new MatrixModel(na, nb, nc, nd, ne, nf);
  }
}

// Rect
export class RectModel {
  zoom;

  constructor(
    public left,
    public top,
    public right,
    public bottom,
  ) {}

  get width() {
    return this.getWidth();
  }

  get height() {
    return this.getHeight();
  }

  getOrigin() {
    return new PointModel(this.min_x(), this.min_y());
  }

  floor() {
    return new RectModel(castFloatToInt(this.min_x()), castFloatToInt(this.min_y()), castFloatToInt(this.max_x()), castFloatToInt(this.max_y()));
  }

  round() {
    return new RectModel(Math.round(this.min_x()), Math.round(this.min_y()), Math.round(this.max_x()), Math.round(this.max_y()));
  }

  offset(dx, dy) {
    this.left += dx;
    this.top += dy;
    this.right += dx;
    this.bottom += dy;
  }

  inflate(dx, dy?) {
    this.left -= dx;
    this.top -= dy;
    this.right += dx;
    this.bottom += dy;
  }

  stretch(dx, dy) {
    this.right = this.left + dx;
    this.bottom = this.top - dy;
  }

  extend(point) {
    this.left = Math.min(this.left, point.x);
    this.top = Math.min(this.top, point.y);
    this.right = Math.max(this.right, point.x);
    this.bottom = Math.max(this.bottom, point.y);
  }

  sort() {
    if (this.left > this.right) {
      const tRight = this.right;
      this.right = this.left;
      this.left = tRight;
    }

    if (this.top > this.bottom) {
      const tBottom = this.bottom;
      this.bottom = this.top;
      this.top = tBottom;
    }
  }

  min_x() {
    return Math.min(this.left, this.right);
  }

  max_x() {
    return Math.max(this.left, this.right);
  }

  min_y() {
    return Math.min(this.top, this.bottom);
  }

  max_y() {
    return Math.max(this.top, this.bottom);
  }

  leftTop() {
    return new PointModel(this.left, this.top);
  }

  leftBottom() {
    return new PointModel(this.left, this.bottom);
  }

  rightTop() {
    return new PointModel(this.right, this.top);
  }

  rightBottom() {
    return new PointModel(this.right, this.bottom);
  }

  getWidth() {
    return this.max_x() - this.min_x();
  }

  getHeight() {
    return this.max_y() - this.min_y();
  }

  getSize() {
    return new SizeModel(this.getWidth(), this.getHeight());
  }

  containsPoint(point) {
    return point.x >= this.min_x() && point.x <= this.max_x() && point.y >= this.min_y() && point.y <= this.max_y();
  }

  containsRect(rect) {
    return this.min_x() <= rect.min_x() && this.max_x() >= rect.max_x() && this.min_y() <= rect.min_y() && this.max_y() >= rect.max_y();
  }

  hasIntersection(rect) {
    return this.min_x() < rect.max_x() && this.min_y() < rect.max_y() && this.max_x() > rect.min_x() && this.max_y() > rect.min_y();
  }

  isAreaEmpty() {
    return this.left === this.right || this.top === this.bottom;
  }

  getCenter() {
    return new PointModel(this.min_x() + this.getWidth() / 2, this.min_y() + this.getHeight() / 2);
  }

  getArea() {
    return this.getWidth() * this.getHeight();
  }

  isEqual(that) {
    return this.min_x() === that.min_x() && this.max_x() === that.max_x() && this.min_y() === that.min_y() && this.max_y() === that.max_y();
  }

  asArray() {
    return [this.left, this.top, this.right, this.bottom];
  }

  copy() {
    return new RectModel(this.left, this.top, this.right, this.bottom);
  }
}

// Point
export class PointModel {
  constructor(
    public x,
    public y,
  ) {}

  static pointsCenter(a, b) {
    return new PointModel(a.x + (b.x - a.x) / 2, a.y + (b.y - a.y) / 2);
  }

  private pointsSquareDistance(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;

    return dx * dx + dy * dy;
  }

  private pointsDistance(a, b) {
    return Math.sqrt(this.pointsSquareDistance(a, b));
  }

  private vecDotProduct(a, b) {
    return a.x * b.x + a.y * b.y;
  }

  private vecAngleBetween(a, b) {
    const alpha = Math.atan2(a.y, a.x);
    const beta = Math.atan2(b.y, b.x);

    return beta - alpha;
  }

  floor() {
    return new PointModel(castFloatToInt(this.x), castFloatToInt(this.y));
  }

  offset(dx, dy) {
    this.x += dx;
    this.y += dy;
  }

  isEqual(value) {
    return value && this.x === value.x && this.y === value.y;
  }

  vecMagnitude() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  vecNormalized() {
    const mag = this.vecMagnitude();

    if (floatEq(mag, 0)) {
      return this.copy();
    }

    return new PointModel(this.x / mag, this.y / mag);
  }

  copy() {
    return new PointModel(this.x, this.y);
  }

  distanceToEdge(a, b) {
    const p = this;
    const l2 = this.pointsSquareDistance(a, b);

    if (l2 === 0) {
      return this.pointsDistance(p, a);
    }

    const t = this.vecDotProduct(new PointModel(p.x - a.x, p.y - a.y), new PointModel(b.y - a.y, b.y - a.y)) / l2;

    if (t < 0) {
      return this.pointsDistance(p, a);
    } else if (t > 1) {
      return this.pointsDistance(p, b);
    }

    return this.pointsDistance(p, new PointModel(a.x + t * (b.x - a.x), a.y + t * (b.y - a.y)));
  }
}

// Quad
export class QuadModel {
  constructor(
    private tl,
    private tr,
    private bl,
    private br,
  ) {}

  top_left() {
    return this.tl.copy();
  }

  top_right() {
    return this.tr.copy();
  }

  bottom_left() {
    return this.bl.copy();
  }

  bottom_right() {
    return this.br.copy();
  }

  top_center() {
    return PointModel.pointsCenter(this.tl, this.tr);
  }

  center_left() {
    return PointModel.pointsCenter(this.tl, this.bl);
  }

  bottom_center() {
    return PointModel.pointsCenter(this.bl, this.br);
  }

  center_right() {
    return PointModel.pointsCenter(this.br, this.tr);
  }

  getCenter() {
    return PointModel.pointsCenter(PointModel.pointsCenter(this.tl, this.br), PointModel.pointsCenter(this.tr, this.bl));
  }

  getBound() {
    const points = [];
    points[0] = this.tl;
    points[1] = this.tr;
    points[2] = this.bl;
    points[3] = this.br;

    const result = {
      left: points[0].x,
      top: points[0].y,
      right: points[0].x,
      bottom: points[0].y,
    };

    for (let i = 1; i < 4; ++i) {
      result.left = Math.min(result.left, points[i].x);
      result.top = Math.min(result.top, points[i].y);
      result.right = Math.max(result.right, points[i].x);
      result.bottom = Math.max(result.bottom, points[i].y);
    }

    return new RectModel(result.left, result.top, result.right, result.bottom);
  }

  containsPoint(point) {
    var bound = this.getBound();
    if (!bound.containsPoint(point)) return false;

    if (point.isEqual(this.tl) || point.isEqual(this.tr) || point.isEqual(this.bl) || point.isEqual(this.br)) return true;

    if (floatEq(0, point.distanceToEdge(this.tl, this.tr))) return true;
    if (floatEq(0, point.distanceToEdge(this.tr, this.br))) return true;
    if (floatEq(0, point.distanceToEdge(this.br, this.bl))) return true;
    if (floatEq(0, point.distanceToEdge(this.bl, this.tl))) return true;

    var inside = false;
    if (
      this.tl.y > point.y != this.tr.y > point.y &&
      point.x < ((this.tr.x - this.tl.x) * (point.y - this.tl.y)) / (this.tr.y - this.tl.y) + this.tl.x
    )
      inside = !inside;
    if (
      this.tr.y > point.y != this.br.y > point.y &&
      point.x < ((this.br.x - this.tr.x) * (point.y - this.tr.y)) / (this.br.y - this.tr.y) + this.tr.x
    )
      inside = !inside;
    if (
      this.br.y > point.y != this.bl.y > point.y &&
      point.x < ((this.bl.x - this.br.x) * (point.y - this.br.y)) / (this.bl.y - this.br.y) + this.br.x
    )
      inside = !inside;
    if (
      this.bl.y > point.y != this.tl.y > point.y &&
      point.x < ((this.tl.x - this.bl.x) * (point.y - this.bl.y)) / (this.tl.y - this.bl.y) + this.bl.x
    )
      inside = !inside;
    return inside;
  }

  hasIntersectionRect(rect) {
    if (rect.containsPoint(this.tl) || rect.containsPoint(this.tr) || rect.containsPoint(this.bl) || rect.containsPoint(this.br)) return true;

    if (lineIntersectsRect(this.tl, this.tr, rect)) return true;
    if (lineIntersectsRect(this.tr, this.br, rect)) return true;
    if (lineIntersectsRect(this.br, this.bl, rect)) return true;
    if (lineIntersectsRect(this.bl, this.tl, rect)) return true;

    return false;
  }

  hasIntersectionQuad(quad) {
    if (this.containsPoint(quad.tl) || this.containsPoint(quad.tr) || this.containsPoint(quad.br) || this.containsPoint(quad.bl)) return true;

    if (quad.containsPoint(this.tl) || quad.containsPoint(this.tr) || quad.containsPoint(this.br) || quad.containsPoint(this.bl)) return true;

    if (
      lineIntersectsLine(this.tl, this.tr, quad.tl, quad.tr) ||
      lineIntersectsLine(this.tl, this.tr, quad.tr, quad.br) ||
      lineIntersectsLine(this.tl, this.tr, quad.br, quad.bl) ||
      lineIntersectsLine(this.tl, this.tr, quad.bl, quad.tl)
    )
      return true;

    if (
      lineIntersectsLine(this.tr, this.br, quad.tl, quad.tr) ||
      lineIntersectsLine(this.tr, this.br, quad.tr, quad.br) ||
      lineIntersectsLine(this.tr, this.br, quad.br, quad.bl) ||
      lineIntersectsLine(this.tr, this.br, quad.bl, quad.tl)
    )
      return true;

    if (
      lineIntersectsLine(this.br, this.bl, quad.tl, quad.tr) ||
      lineIntersectsLine(this.br, this.bl, quad.tr, quad.br) ||
      lineIntersectsLine(this.br, this.bl, quad.br, quad.bl) ||
      lineIntersectsLine(this.br, this.bl, quad.bl, quad.tl)
    )
      return true;

    if (
      lineIntersectsLine(this.bl, this.tl, quad.tl, quad.tr) ||
      lineIntersectsLine(this.bl, this.tl, quad.tr, quad.br) ||
      lineIntersectsLine(this.bl, this.tl, quad.br, quad.bl) ||
      lineIntersectsLine(this.bl, this.tl, quad.bl, quad.tl)
    )
      return true;

    return false;
  }

  offset(dx, dy) {
    this.tl.x += dx;
    this.tl.y += dy;
    this.tr.x += dx;
    this.tr.y += dy;
    this.bl.x += dx;
    this.bl.y += dy;
    this.br.x += dx;
    this.br.y += dy;
  }

  isRect() {
    const rect1 = new RectModel(this.tl.x, this.tl.y, this.br.x, this.br.y);
    rect1.sort();

    const rect2 = new RectModel(this.tr.x, this.tr.y, this.bl.x, this.bl.y);
    rect2.sort();

    return rect1.isEqual(rect2);
  }

  isEqual(that) {
    if (this == that) return true;

    return (
      this.tl.x == that.tl.x &&
      this.tr.x == that.tr.x &&
      this.bl.x == that.bl.x &&
      this.br.x == that.br.x &&
      this.tl.y == that.tl.y &&
      this.tr.y == that.tr.y &&
      this.bl.y == that.bl.y &&
      this.br.y == that.br.y
    );
  }

  copy() {
    return new QuadModel(this.tl, this.tr, this.bl, this.br);
  }
}

@Injectable()
export class SlMath {
  static unionRects(a, b) {
    return new RectModel(
      Math.min(a.min_x(), b.min_x()),
      Math.min(a.min_y(), b.min_y()),
      Math.max(a.max_x(), b.max_x()),
      Math.max(a.max_y(), b.max_y()),
    );
  }

  static floatLt(a, b) {
    return floatLt(a, b);
  }

  static floatGt(a, b) {
    return floatGt(a, b);
  }

  static floatEq(a, b) {
    return floatEq(a, b);
  }

  static degreeToRadian(degrees) {
    return parseFloat(degrees) * (Math.PI / 180.0);
  }

  static size(width = 0, height = 0): SizeModel {
    return new SizeModel(width, height);
  }

  static identityMatrix(): MatrixModel {
    const a = 1;
    const b = 0;
    const c = 0;
    const d = 1;
    const e = 0;
    const f = 0;

    return new MatrixModel(a, b, c, d, e, f);
  }

  static point(x = 0, y = 0) {
    return new PointModel(x, y);
  }

  static castFloatToInt(fValue) {
    return castFloatToInt(fValue);
  }

  static intersectRects(a, b) {
    if (!a.hasIntersection(b)) {
      return new RectModel(0, 0, 0, 0);
    }

    if (a.isAreaEmpty()) {
      return a;
    }

    if (b.isAreaEmpty()) {
      return b;
    }

    const i = a.copy();

    i.left = Math.max(a.min_x(), b.min_x());
    i.top = Math.max(a.min_y(), b.min_y());
    i.right = Math.min(a.max_x(), b.max_x());
    i.bottom = Math.min(a.max_y(), b.max_y());

    return i;
  }

  //Matrix
  static matrix(a?, b?, c?, d?, e?, f?): MatrixModel {
    if (
      ext.isUndefinedOrNull(a) &&
      ext.isUndefinedOrNull(b) &&
      ext.isUndefinedOrNull(c) &&
      ext.isUndefinedOrNull(d) &&
      ext.isUndefinedOrNull(e) &&
      ext.isUndefinedOrNull(f)
    ) {
      return new MatrixModel(1, 0, 0, 1, 0, 0);
    }

    return new MatrixModel(a, b, c, d, e, f);
  }

  static translationMatrix(x, y) {
    return new MatrixModel(1, 0, 0, 1, x, y);
  }

  static scalingMatrix(x, y, origin?) {
    let orgX = 0;
    let orgY = 0;

    if (!ext.isUndefinedOrNull(origin)) {
      orgX = origin.x;
      orgY = origin.y;
    }

    return new MatrixModel(x, 0, 0, y, (1 - x) * orgX, (1 - y) * orgY);
  }

  static rotationMatrix(radian, origin?) {
    const sina = Math.sin(radian);
    const cosa = Math.cos(radian);

    let dx = 0;
    let dy = 0;

    if (!ext.isUndefinedOrNull(origin)) {
      dx = origin.x * (1 - cosa) + origin.y * sina;
      dy = origin.y * (1 - cosa) - origin.x * sina;
    }

    return new MatrixModel(cosa, sina, -sina, cosa, dx, dy);
  }

  // Rect
  static rect(left = 0, top = 0, right = 0, bottom = 0): RectModel {
    return new RectModel(left, top, right, bottom);
  }

  static adjustRect(rect) {
    if (!rect) {
      return rect;
    }

    const rectI = new RectModel(0, 0, 0, 0);

    rectI.left = castFloatToInt(Math.floor(rect.min_x()));
    rectI.top = castFloatToInt(Math.floor(rect.min_y()));
    rectI.right = castFloatToInt(Math.ceil(rect.max_x()));
    rectI.bottom = castFloatToInt(Math.ceil(rect.max_y()));

    return /*convertRectIToRectF(*/ rectI /*)*/;
  }

  static quadFromRect(rect): QuadModel {
    if (ext.isUndefinedOrNull(rect)) {
      rect = new RectModel(0, 0, 0, 0);
    }

    const tl = new PointModel(rect.left, rect.top);
    const tr = new PointModel(rect.right, rect.top);
    const bl = new PointModel(rect.left, rect.bottom);
    const br = new PointModel(rect.right, rect.bottom);

    return new QuadModel(tl, tr, bl, br);
  }

  static isRectsOnSameLine(a, b, threshold) {
    // if coordinate difference is below the 'threshold' they are treated as being on the same line
    return Math.abs(a.top - b.top) < threshold && Math.abs(a.bottom - b.bottom) < threshold && a.left < b.left && a.right < b.right;
  }
}
